import { motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { IMG, waLink } from "@/lib/content";
import { MaskLine } from "@/components/site/Reveal";

export const Hero = () => {
  const { t } = useLang();

  return (
    <section id="top" className="relative h-[100svh] w-full overflow-hidden bg-navy" data-testid="hero">
      {/* Static image — no parallax, boat always visible */}
      <img
        src={IMG.heroTwilight}
        alt="La Rondine all'ancora al crepuscolo in una baia di Ischia"
        fetchpriority="high"
        decoding="async"
        className="absolute inset-0 h-full w-full object-cover object-center brightness-[1.15] contrast-[1.02] md:brightness-100 md:contrast-100"
      />
      {/* Readability wash focused on the top so the boat stays clear */}
      <div className="absolute inset-0 bg-gradient-to-b from-navy/70 via-navy/10 to-navy/30" />

      <div className="relative z-10 mx-auto flex h-full max-w-[1400px] flex-col justify-start items-start px-5 md:px-10 pt-24 md:pt-32 text-left">
        <div className="max-w-xs sm:max-w-sm md:max-w-md">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 1 }}
            className="mb-5 flex items-center gap-3"
          >
            <span className="h-px w-10 bg-gold" />
            <span className="text-xs uppercase tracking-[0.3em] text-canvas/85">{t.hero.overline}</span>
          </motion.div>

          <h1 className="font-serif font-light text-canvas leading-[1.0] tracking-tight text-3xl sm:text-4xl lg:text-5xl drop-shadow-[0_2px_12px_rgba(10,25,47,0.45)]">
            {t.hero.titleLines.map((line, i, arr) => (
              <MaskLine key={line + i} delay={0.5 + i * 0.14} className={i === arr.length - 1 ? "italic" : ""}>
                {line}
              </MaskLine>
            ))}
          </h1>

          <div className="mt-5 md:mt-6 flex flex-col items-start gap-4 md:gap-5">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2, duration: 0.9 }}
              className="flex items-start gap-3"
            >
              <span className="mt-2 h-px w-8 shrink-0 bg-gold" />
              <p className="max-w-xs text-sm md:text-base font-light leading-relaxed text-canvas/90 drop-shadow-[0_1px_8px_rgba(10,25,47,0.5)]">
                {t.hero.sub}
              </p>
            </motion.div>
          </div>
        </div>
      </div>

      <motion.a
        href={waLink(t.waMsg)}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="hero-book-button"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.4, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        className="group absolute bottom-6 right-5 md:bottom-10 md:right-10 z-20 inline-flex items-center gap-3 rounded-full border border-[#C9A24B] bg-navy/40 px-6 py-3.5 text-[#E7C877] backdrop-blur-md shadow-[0_10px_34px_-10px_rgba(0,0,0,0.7)] transition-colors duration-300 hover:bg-[#C9A24B] hover:text-navy"
      >
        <span className="text-xs uppercase tracking-[0.18em] font-medium">{t.hero.cta}</span>
        <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
      </motion.a>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 left-5 md:left-10 z-10 hidden md:flex items-center gap-2 text-canvas/70"
      >
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>↓</motion.span>
        <span className="text-[10px] uppercase tracking-[0.25em]">{t.hero.scroll}</span>
      </motion.div>
    </section>
  );
};
